import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Header from '../components/header';

const API_BASE = '/api';

const EditProductPage = () => {
  const navigate = useNavigate();
  const { productId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const descriptionRef = useRef(null);
  const fileInputRef = useRef(null);

  // Store information
  const [store, setStore] = useState(null);
  
  // Categories
  const [categories, setCategories] = useState([]);
  
  // Product fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  
  // Item type - NEW
  const [itemType, setItemType] = useState('product');
  
  // Original product data for comparison
  const [originalProduct, setOriginalProduct] = useState(null);
  
  // Image management states
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Check if any fields have been modified
  const hasChanges = () => {
    if (!originalProduct) return false;
    
    const hasFieldChanges = 
      name !== originalProduct.name ||
      description !== originalProduct.description ||
      parseFloat(price) !== parseFloat(originalProduct.price) ||
      (categoryId || null) !== (originalProduct.category_id || null) ||
      (externalUrl || '') !== (originalProduct.external_url || '') ||
      (itemType || 'product') !== (originalProduct.item_type || 'product'); // Added item_type check
    
    const hasImageChanges = 
      newImages.length > 0 || 
      imagesToDelete.length > 0 || 
      JSON.stringify(existingImages.map(img => ({id: img.id, is_primary: img.is_primary}))) !== 
      JSON.stringify((originalProduct.images || []).map(img => ({id: img.id, is_primary: img.is_primary})));
    
    return hasFieldChanges || hasImageChanges;
  };

  // Fetch user's store information
  useEffect(() => {
    const fetchStoreInfo = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }
        
        const response = await fetch(`${API_BASE}/user/store`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const storeData = await response.json();
          setStore(storeData);
        } else if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('token');
          navigate('/login');
        }
      } catch (error) {
        console.error('Error fetching store info:', error);
      }
    };

    fetchStoreInfo();
  }, [navigate]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE}/categories`);
        if (response.ok) {
          const data = await response.json();
          setCategories(data);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    
    fetchCategories();
  }, []);

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      if (!store) return;
      
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await fetch(
          `${API_BASE}/stores/${store.url_name}/products/${productId}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.ok) {
          const productData = await response.json();
          
          // Set form fields
          setName(productData.name || '');
          setDescription(productData.description || '');
          setPrice(productData.price || '0');
          setCategoryId(productData.category_id || '');
          setExternalUrl(productData.external_url || '');
          setItemType(productData.item_type || 'product'); // Set item_type from product data
          
          // Set images
          setExistingImages(productData.images || []);
          
          // Store original for comparison
          setOriginalProduct(productData);
        } else if (response.status === 404) {
          setError('Product not found');
        } else if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('token');
          navigate('/login');
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        setError('Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    if (store) {
      fetchProduct();
    }
  }, [store, productId, navigate]);

  // Handle file selection
  const handleFiles = (files) => {
    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      setError('Please select only image files');
      setTimeout(() => setError(null), 3000);
      return;
    }

    const oversizedFiles = imageFiles.filter(file => file.size > 5 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setError('Some files are too large. Maximum size is 5MB per image');
      setTimeout(() => setError(null), 3000);
      return;
    }

    const totalImages = existingImages.length - imagesToDelete.length + newImages.length + imageFiles.length;
    if (totalImages > 10) {
      setError('Maximum 10 images allowed per product');
      setTimeout(() => setError(null), 3000);
      return;
    }

    const newImageObjects = imageFiles.map((file, index) => ({
      id: `new-${Date.now()}-${index}`,
      file: file,
      preview: URL.createObjectURL(file),
      name: file.name,
      is_primary: false
    }));

    setNewImages(prev => [...prev, ...newImageObjects]);
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  // Remove existing image (mark for deletion)
  const removeExistingImage = (imageId) => {
    setImagesToDelete(prev => [...prev, imageId]);
  };

  // Restore image (unmark for deletion)
  const restoreImage = (imageId) => {
    setImagesToDelete(prev => prev.filter(id => id !== imageId));
  };

  // Remove new image
  const removeNewImage = (imageId) => {
    const imageToRemove = newImages.find(img => img.id === imageId);
    if (imageToRemove && imageToRemove.preview) {
      URL.revokeObjectURL(imageToRemove.preview);
    }
    setNewImages(prev => prev.filter(img => img.id !== imageId));
  };

  // Set primary image
  const setPrimaryImage = async (imageId, isNewImage = false) => {
    if (isNewImage) {
      // For new images, just update local state
      setNewImages(prev => prev.map(img => ({
        ...img,
        is_primary: img.id === imageId
      })));
      setExistingImages(prev => prev.map(img => ({
        ...img,
        is_primary: false
      })));
    } else {
      // For existing images, make API call
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${API_BASE}/products/${productId}/images/${imageId}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ is_primary: true })
          }
        );

        if (response.ok) {
          setExistingImages(prev => prev.map(img => ({
            ...img,
            is_primary: img.id === imageId
          })));
          setNewImages(prev => prev.map(img => ({
            ...img,
            is_primary: false
          })));
        } else {
          throw new Error('Failed to set primary image');
        }
      } catch (error) {
        console.error('Error setting primary image:', error);
        setError('Failed to set primary image');
        setTimeout(() => setError(null), 3000);
      }
    }
  };

  // Simple markdown helper
  const insertMarkdown = (syntax) => {
    const textarea = descriptionRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = description;
    
    let newText = '';
    let newCursorPos = start;
    
    switch(syntax) {
      case 'bold':
        const selectedText = text.substring(start, end) || 'bold text';
        newText = text.substring(0, start) + '**' + selectedText + '**' + text.substring(end);
        newCursorPos = start + 2;
        break;
      case 'italic':
        const selectedItalic = text.substring(start, end) || 'italic text';
        newText = text.substring(0, start) + '*' + selectedItalic + '*' + text.substring(end);
        newCursorPos = start + 1;
        break;
      case 'bullet':
        const prefix = start > 0 && text[start-1] !== '\n' ? '\n' : '';
        newText = text.substring(0, start) + prefix + '• ' + text.substring(end);
        newCursorPos = start + prefix.length + 2;
        break;
      default:
        return;
    }
    
    setDescription(newText);
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Validate URL format
  const isValidUrl = (string) => {
    if (!string) return true;
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Item type helpers
  const getItemTypeLabel = (type) => {
    switch(type) {
      case 'product': return 'Product';
      case 'service': return 'Service';
      case 'experience': return 'Experience';
      default: return 'Product';
    }
  };

  const getItemTypeDescription = (type) => {
    switch(type) {
      case 'product': 
        return 'Physical or digital items your customers can purchase';
      case 'service': 
        return 'Services you provide such as consultations, repairs, or professional work';
      case 'experience': 
        return 'Activities or events customers can participate in or attend';
      default: 
        return '';
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!hasChanges()) {
      setError('No changes detected');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setError(null);
    setSuccess(false);
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Validate price
      const floatPrice = parseFloat(price);
      if (isNaN(floatPrice) || floatPrice < 0) {
        setError('Please enter a valid price');
        setSaving(false);
        return;
      }

      // Validate external URL
      if (externalUrl && !isValidUrl(externalUrl)) {
        setError('Please enter a valid URL');
        setSaving(false);
        return;
      }

      // 1. Update product data
      const updateData = {
        name: name.trim(),
        description: description.trim(),
        price: floatPrice,
        category_id: categoryId || null,
        external_url: externalUrl || null,
        item_type: itemType // Add item_type to update data
      };

      const response = await fetch(
        `${API_BASE}/stores/${store.url_name}/products/${productId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(updateData)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update product');
      }

      // 2. Delete marked images
      for (const imageId of imagesToDelete) {
        await fetch(`${API_BASE}/products/${productId}/images/${imageId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }

      // 3. Upload new images
      if (newImages.length > 0) {
        const formData = new FormData();
        
        // Find if any new image is marked as primary
        const primaryNewImage = newImages.find(img => img.is_primary);
        
        // Add images in order (primary first if exists)
        if (primaryNewImage) {
          formData.append('images', primaryNewImage.file);
          newImages.filter(img => !img.is_primary).forEach(img => {
            formData.append('images', img.file);
          });
        } else {
          newImages.forEach(img => {
            formData.append('images', img.file);
          });
        }

        await fetch(`${API_BASE}/products/${productId}/images`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
      }

      // Clean up preview URLs
      newImages.forEach(img => {
        if (img.preview) URL.revokeObjectURL(img.preview);
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);

    } catch (err) {
      console.error('Error updating product:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (hasChanges()) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        return;
      }
    }
    // Clean up preview URLs
    newImages.forEach(img => {
      if (img.preview) URL.revokeObjectURL(img.preview);
    });
    navigate('/dashboard');
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Loading state
  if (loading) {
    return (
      <>
        <Header />
        <div className="container py-5">
          <div className="d-flex justify-content-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Calculate active images
  const activeExistingImages = existingImages.filter(img => !imagesToDelete.includes(img.id));
  const totalActiveImages = activeExistingImages.length + newImages.length;
  const hasPrimaryImage = [...activeExistingImages, ...newImages].some(img => img.is_primary);

  return (
    <>
      <Header />
      <div className="container py-5">
        
        {/* Store Header */}
        {store && (
          <div className="mb-4 pb-3 border-bottom">
            <Link 
              to={`/${store.city}/${store.url_name}`} 
              className="text-decoration-none d-inline-flex align-items-center"
            >
              {store.profile_image_url ? (
                <img 
                  src={store.profile_image_url.startsWith('/') ? 
                    `https://storetoyou.ca${store.profile_image_url}` : 
                    store.profile_image_url}
                  alt={store.name}
                  className="rounded-circle me-3"
                  style={{
                    width: '60px',
                    height: '60px',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <div 
                  className="rounded-circle me-3 bg-light d-flex align-items-center justify-content-center"
                  style={{
                    width: '60px',
                    height: '60px',
                    fontSize: '24px',
                    color: '#666'
                  }}
                >
                  {store.name?.charAt(0) || ''}
                </div>
              )}
              <h3 className="mb-0 text-dark">{store.name}</h3>
            </Link>
          </div>
        )}

        <h1 className="mb-4">Edit {getItemTypeLabel(itemType)}</h1>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success" role="alert">
            {getItemTypeLabel(itemType)} updated successfully! Redirecting to dashboard...
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* ITEM TYPE SELECTOR - NEW */}
          <div className="mb-4">
            <label className="form-label">Item Type *</label>
            <div className="d-flex flex-wrap gap-2">
              {['product', 'service', 'experience'].map(type => (
                <div 
                  key={type}
                  className={`card ${itemType === type ? 'border-primary' : 'border'}`}
                  style={{ 
                    minWidth: '180px', 
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out'
                  }}
                  onClick={() => setItemType(type)}
                >
                  <div className="card-body p-3">
                    <h5 className="card-title mb-1">
                      {itemType === type && <i className="fas fa-check-circle text-primary me-2"></i>}
                      {getItemTypeLabel(type)}
                    </h5>
                    <p className="card-text small text-muted mb-0">
                      {getItemTypeDescription(type)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* IMAGE MANAGEMENT SECTION */}
          <div className="mb-4">
            <label className="form-label">{getItemTypeLabel(itemType)} Images</label>
            <div className="form-text mb-3">
              Manage your {getItemTypeLabel(itemType).toLowerCase()} images. You can have up to 10 images. 
              {!hasPrimaryImage && totalActiveImages > 0 && (
                <span className="text-warning ms-2">
                  <i className="fas fa-exclamation-triangle me-1"></i>
                  No primary image selected. The first image will be used.
                </span>
              )}
            </div>

            {/* Existing Images */}
            {existingImages.length > 0 && (
              <div className="mb-3">
                <h6 className="mb-2">Current Images</h6>
                <div className="row g-2">
                  {existingImages.map((img) => {
                    const isMarkedForDeletion = imagesToDelete.includes(img.id);
                    return (
                      <div key={img.id} className="col-md-3 col-sm-4 col-6">
                        <div className={`card ${isMarkedForDeletion ? 'opacity-50' : ''}`}>
                          <div className="position-relative">
                            <img
                              src={img.image_url.startsWith('/') ? 
                                `https://storetoyou.ca${img.image_url}` : 
                                img.image_url}
                              alt={getItemTypeLabel(itemType)}
                              className="card-img-top"
                              style={{
                                height: '120px',
                                objectFit: 'cover',
                                filter: isMarkedForDeletion ? 'grayscale(100%)' : 'none'
                              }}
                            />
                            
                            {img.is_primary && !isMarkedForDeletion && (
                              <span className="position-absolute top-0 start-0 m-1 badge bg-success">
                                <i className="fas fa-star"></i> Primary
                              </span>
                            )}
                            
                            {isMarkedForDeletion && (
                              <div className="position-absolute top-50 start-50 translate-middle">
                                <span className="badge bg-danger">DELETED</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="card-body p-2">
                            <div className="btn-group btn-group-sm w-100" role="group">
                              {!isMarkedForDeletion ? (
                                <>
                                  {!img.is_primary && (
                                    <button
                                      type="button"
                                      className="btn btn-outline-primary"
                                      onClick={() => setPrimaryImage(img.id, false)}
                                      title="Set as primary"
                                    >
                                      <i className="fas fa-star"></i>
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger"
                                    onClick={() => removeExistingImage(img.id)}
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-outline-success w-100"
                                  onClick={() => restoreImage(img.id)}
                                >
                                  <i className="fas fa-undo"></i> Restore
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* New Images */}
            {newImages.length > 0 && (
              <div className="mb-3">
                <h6 className="mb-2">New Images to Upload</h6>
                <div className="row g-2">
                  {newImages.map((img) => (
                    <div key={img.id} className="col-md-3 col-sm-4 col-6">
                      <div className="card border-success">
                        <div className="position-relative">
                          <img
                            src={img.preview}
                            alt="New"
                            className="card-img-top"
                            style={{
                              height: '120px',
                              objectFit: 'cover'
                            }}
                          />
                          
                          {img.is_primary && (
                            <span className="position-absolute top-0 start-0 m-1 badge bg-success">
                              <i className="fas fa-star"></i> Primary
                            </span>
                          )}
                          
                          <span className="position-absolute top-0 end-0 m-1 badge bg-info">
                            NEW
                          </span>
                        </div>
                        
                        <div className="card-body p-2">
                          <small className="d-block text-truncate">{img.name}</small>
                          <div className="btn-group btn-group-sm w-100 mt-1" role="group">
                            {!img.is_primary && (
                              <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={() => setPrimaryImage(img.id, true)}
                                title="Set as primary"
                              >
                                <i className="fas fa-star"></i>
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn btn-outline-danger"
                              onClick={() => removeNewImage(img.id)}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Area */}
            {totalActiveImages < 10 && (
              <div
                className={`border rounded p-3 text-center ${
                  dragOver ? 'border-primary bg-light' : 'border-secondary'
                }`}
                style={{
                  borderStyle: 'dashed',
                  borderWidth: '2px',
                  cursor: 'pointer'
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <i className="fas fa-cloud-upload-alt fa-2x mb-2 text-muted"></i>
                <p className="mb-0">
                  Click or drag images here to add more
                  <br />
                  <small className="text-muted">
                    ({totalActiveImages}/10 images) Max 5MB per image
                  </small>
                </p>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFiles(e.target.files)}
              style={{ display: 'none' }}
            />
          </div>

          {/* NAME */}
          <div className="mb-3">
            <label className="form-label">{getItemTypeLabel(itemType)} Name *</label>
            <input
              type="text"
              className="form-control"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={`Enter ${getItemTypeLabel(itemType).toLowerCase()} name`}
            />
          </div>

          {/* DESCRIPTION */}
          <div className="mb-3">
            <label className="form-label">Description *</label>
            
            <div className="mb-2">
              <small className="text-muted d-block mb-2">
                Quick formatting: Add <strong>**text**</strong> for bold, <em>*text*</em> for italic, or use • for bullet points
              </small>
              
              <div className="btn-group btn-group-sm mb-2" role="group">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => insertMarkdown('bold')}
                >
                  <strong>B</strong> Bold
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => insertMarkdown('italic')}
                >
                  <em>I</em> Italic
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => insertMarkdown('bullet')}
                >
                  • Bullet
                </button>
              </div>
            </div>

            <textarea
              ref={descriptionRef}
              className="form-control"
              rows="5"
              required
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={`Describe your ${itemType} here...`}
            />
          </div>

          {/* PRICE */}
          <div className="mb-3">
            <label className="form-label">Price *</label>
            <div className="input-group">
              <span className="input-group-text">$</span>
              <input
                type="number"
                className="form-control"
                min="0"
                step="0.01"
                required
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            </div>
            {itemType === 'service' && (
              <small className="form-text text-muted">
                For variable pricing, enter a starting price or average price.
              </small>
            )}
          </div>

          {/* CATEGORY */}
          <div className="mb-3">
            <label className="form-label">Category</label>
            <select
              className="form-select"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">No Category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* EXTERNAL URL */}
          <div className="mb-3">
            <label className="form-label">External URL (optional)</label>
            <input
              type="url"
              className="form-control"
              placeholder="https://example.com/product-page"
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
            />
            <div className="form-text">
              {itemType === 'product' && 'Add a link to your product page or external listing (e.g., Etsy, Amazon, your website)'}
              {itemType === 'service' && 'Add a link to your booking system, scheduling page, or service details'}
              {itemType === 'experience' && 'Add a link to booking information, event details, or registration page'}
            </div>
          </div>

          {/* BUTTONS */}
          <div className="d-flex justify-content-between align-items-center">
            <div>
              {hasChanges() && (
                <span className="text-warning">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  You have unsaved changes
                </span>
              )}
            </div>
            
            <div>
              <button
                type="button"
                className="btn btn-outline-secondary me-2"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving || !hasChanges()}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-2"></i>
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default EditProductPage;
